<template>
  <BaseDialog contentClass="field-mapping-dialog" ref="dialog" :show-actions="true">
    <template slot="title">{{
      isEdit
        ? $t('page.admin-sync-profiles.mapping-dialog.edit-title')
        : $t('page.admin-sync-profiles.mapping-dialog.add-title')
    }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <!-- CRM Field + Scope -->
        <div class="field-mapping-dialog__row">
          <FormInput
            class="field-mapping-dialog__code-input"
            :label="$t('page.admin-sync-profiles.mapping-fields.crm-field-name')"
            :is-required="true"
            has-gray-background
            v-model="form.crmFieldName"
            :error="crmFieldNameError"
            @input="touch('crmFieldName')"
          />
          <div class="field-mapping-dialog__field">
            <FormLabel :is-required="true">{{ $t('page.admin-sync-profiles.mapping-fields.entity-scope') }}</FormLabel>
            <BasePillGroup
              class="field-mapping-dialog__scope-pills"
              multiple
              :options="entityScopeOptions"
              :value="form.entityScope"
              @input="form.entityScope = $event"
            />
          </div>
        </div>

        <!-- Field Type + Sync Mode -->
        <div class="field-mapping-dialog__row">
          <BaseSelect
            name="fieldType"
            :label="$t('page.admin-sync-profiles.mapping-fields.field-type')"
            :items="fieldTypeOptions"
            item-text="label"
            item-value="value"
            :value="form.fieldType"
            @change="onFieldTypeChange"
          />
          <BaseSelect
            name="syncMode"
            :label="$t('page.admin-sync-profiles.mapping-fields.sync-mode')"
            :items="syncModeOptions"
            item-text="label"
            item-value="value"
            :value="form.syncMode"
            :hint="syncModeHint"
            @change="(v) => (form.syncMode = v)"
          />
        </div>

        <!-- Boolean field — literal-only true/false picker. Mirrors the BE's strict parse
             ('true' / 'false' only); if you need a path-resolved boolean, use string field
             type with `{{path.to.boolean}}` and the engine still serialises the right shape. -->
        <div v-if="isBoolean" class="field-mapping-dialog__value-group">
          <FormLabel :is-required="true">
            {{ $t('page.admin-sync-profiles.mapping-fields.value') }}
          </FormLabel>
          <BasePillGroup
            class="field-mapping-dialog__scope-pills"
            :options="booleanValueOptions"
            :value="form.valueExpression"
            @input="setBooleanValue($event)"
          />
          <div v-if="valueExpressionError" class="field-mapping-dialog__error">{{ valueExpressionError }}</div>
        </div>

        <!-- Value Expression (hidden for lookup/choice and boolean — those have dedicated UIs above/below). -->
        <div v-if="!isLookupOrChoice && !isBoolean" class="field-mapping-dialog__value-group">
          <ValueExpressionInput
            ref="valueExpressionInput"
            :label="$t('page.admin-sync-profiles.mapping-fields.value-expression')"
            :is-required="true"
            :is-textarea="form.fieldType === 'string'"
            :value="form.valueExpression"
            :hint="valueExpressionHint"
            :error="valueExpressionError"
            @input="onValueExpressionInput"
          >
            <template #top-right>
              <button
                type="button"
                class="field-mapping-dialog__template-btn field-mapping-dialog__insert-var-btn"
                :title="$t('page.admin-sync-profiles.mapping-fields.insert-variable-tooltip')"
                @mousedown.prevent
                @click="onInsertVariable"
              >
                {{ $t('page.admin-sync-profiles.mapping-fields.insert-variable') }}
              </button>
              <button type="button" class="field-mapping-dialog__template-btn" @click="helpOpen = true">
                {{ $t('page.admin-sync-profiles.field-mapping-help.open-button') }}
              </button>
            </template>
          </ValueExpressionInput>
          <div v-if="valueExpressionWarning" class="field-mapping-dialog__warning">
            {{ valueExpressionWarning }}
          </div>
          <!--
            Key by mapping ID so switching to a different mapping (close → reopen)
            tears down the panel and clears the previous preview result. The opportunity
            selection is restored from localStorage on remount, so the user's last pick
            is preserved across the swap.
          -->
          <ValueExpressionTestPanel
            :key="mapping ? mapping.id : 'new'"
            :value-expression="form.valueExpression"
            :profile-id="profileId"
          />
        </div>

        <!-- Lookup / Choice fields -->
        <div v-if="isLookupOrChoice" class="field-mapping-dialog__form-section">
          <div class="field-mapping-dialog__form-section-title">
            {{ $t('page.admin-sync-profiles.mapping-fields.form-display-section') }}
          </div>

          <FormInput
            class="field-mapping-dialog__generated-expression field-mapping-dialog__code-input"
            :label="$t('page.admin-sync-profiles.mapping-fields.value-expression')"
            :is-required="true"
            has-gray-background
            v-model="form.valueExpression"
            :hint="$t('page.admin-sync-profiles.mapping-fields.hint-generated-value-expression')"
            :error="valueExpressionError"
            @input="touch('valueExpression')"
          />

          <div class="field-mapping-dialog__row">
            <FormInput
              :label="$t('page.admin-sync-profiles.mapping-fields.display-order')"
              :is-required="true"
              has-gray-background
              type="number"
              v-model.number="form.displayOrder"
              :error="displayOrderError"
              @input="touch('displayOrder')"
            />
            <FormInput
              :label="$t('page.admin-sync-profiles.mapping-fields.label')"
              :is-required="true"
              has-gray-background
              v-model="form.label"
              :error="labelError"
              @input="touch('label')"
            />
          </div>

          <div class="field-mapping-dialog__field">
            <div class="field-mapping-dialog__label-row">
              <FormLabel :is-required="true">
                {{ $t('page.admin-sync-profiles.mapping-fields.field-config') }}
              </FormLabel>
              <button type="button" class="field-mapping-dialog__template-btn" @click="fillOptionsTemplate">
                {{ $t('page.admin-sync-profiles.mapping-fields.field-config-fill-template') }}
              </button>
            </div>
            <textarea
              class="field-mapping-dialog__textarea"
              :class="{ 'field-mapping-dialog__textarea--error': fieldConfigError }"
              v-model="form.fieldConfig"
              :placeholder="fieldConfigPlaceholder"
              rows="4"
              @input="touch('fieldConfig')"
            />
            <div class="field-mapping-dialog__hint">
              {{ fieldConfigHint }}
            </div>
            <div v-if="fieldConfigError" class="field-mapping-dialog__error">{{ fieldConfigError }}</div>
          </div>
        </div>

        <div class="field-mapping-dialog__actions">
          <BaseRoundButton
            v-if="isEdit"
            :size="36"
            icon="trash-2"
            is-white
            is-red
            :tooltip-text="$t('actions.delete')"
            @click.native="$emit('delete-requested', mapping)"
          />
          <div class="field-mapping-dialog__actions-right">
            <FormButton is-outlined @click.native="closeDialog">{{ $t('actions.cancel') }}</FormButton>
            <FormButton @click.native="onSubmit" :disabled="isFormInvalid">{{ $t('actions.save') }}</FormButton>
          </div>
        </div>
      </form>
      <ValueExpressionHelpDrawer :visible="helpOpen" @close="helpOpen = false" />
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import BasePillGroup from '@/components/base/BasePillGroup';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import BaseSelect from '@/components/base/BaseSelect';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput';
import FormLabel from '@/components/form/FormLabel';
import ValueExpressionHelpDrawer from '@/components/page-admin-sync-profiles/ValueExpressionHelpDrawer';
import ValueExpressionInput from '@/components/page-admin-sync-profiles/ValueExpressionInput';
import ValueExpressionTestPanel from '@/components/page-admin-sync-profiles/ValueExpressionTestPanel';
import { mapActions, mapGetters } from 'vuex';

const LOOKUP_TEMPLATE = {
  bindPath: 'accounts',
  lookupType: 'account',
  allowCreate: true,
};

const LOOKUP_TEMPLATE_CONTACT = {
  bindPath: 'contacts',
  lookupType: 'contact',
  accountExpression: 'externalAccountId', // optional — remove to search all contacts globally
  allowCreate: true,
};

const CHOICE_TEMPLATE = {
  entityName: 'opportunity',
};

export default {
  name: 'FieldMappingDialog',
  components: {
    BaseDialog,
    BasePillGroup,
    BaseRoundButton,
    BaseSelect,
    FormButton,
    FormInput,
    FormLabel,
    ValueExpressionHelpDrawer,
    ValueExpressionInput,
    ValueExpressionTestPanel,
  },
  props: {
    profileId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      mapping: null,
      submitted: false,
      form: this.emptyForm(),
      formLoading: false,
      touched: {}, // tracks which fields the user has interacted with
      helpOpen: false,
    };
  },
  computed: {
    ...mapGetters('adminSyncProfileFieldMappings', ['fieldMappings']),
    isEdit() {
      return this.mapping !== null;
    },
    entityScopeOptions() {
      return [
        { value: 'opportunity', label: this.$t('page.admin-sync-profiles.mapping-fields.scope-opportunity') },
        { value: 'quote', label: this.$t('page.admin-sync-profiles.mapping-fields.scope-quote') },
      ];
    },
    booleanValueOptions() {
      return [
        { value: 'true', label: this.$t('page.admin-sync-profiles.mapping-fields.boolean-true') },
        { value: 'false', label: this.$t('page.admin-sync-profiles.mapping-fields.boolean-false') },
      ];
    },
    isLookupOrChoice() {
      return ['lookup', 'choice'].includes(this.form.fieldType);
    },
    isBoolean() {
      return this.form.fieldType === 'boolean';
    },
    nextDisplayOrder() {
      const scoped = this.fieldMappings.filter(
        (m) =>
          ['lookup', 'choice'].includes(m.fieldType) &&
          m.entityScope &&
          m.entityScope.some((s) => this.form.entityScope.includes(s)) &&
          (!this.isEdit || m.id !== this.mapping.id),
      );
      const taken = new Set(scoped.map((m) => m.displayOrder || 0));
      let order = 1;
      while (taken.has(order)) order++;
      return order;
    },

    // ── Hints ──────────────────────────────────────────────────────────────
    valueExpressionHint() {
      if (this.form.fieldType === 'lookup') {
        return this.$t('page.admin-sync-profiles.mapping-fields.hint-value-lookup');
      }
      if (this.form.fieldType === 'choice') {
        return this.$t('page.admin-sync-profiles.mapping-fields.hint-value-choice');
      }
      return this.$t('page.admin-sync-profiles.mapping-fields.hint-value-expression');
    },
    valueExpressionWarning() {
      if (this.isLookupOrChoice) return null;
      const v = (this.form.valueExpression || '').trim();
      if (!v) return null;
      // No warning when the value is wrapped in {{ }} or is a bare numeric literal for a number field.
      if (/\{\{[\s\S]*\}\}/.test(v)) return null;
      if (this.form.fieldType === 'number' && v !== '' && !isNaN(Number(v))) return null;
      // Looks like a path / array expression but the user forgot the braces.
      if (v.includes('.') || v.includes('[')) {
        return this.$t('page.admin-sync-profiles.mapping-fields.hint-value-expression-warning');
      }
      return null;
    },
    syncModeHint() {
      const map = {
        ALWAYS: this.$t('page.admin-sync-profiles.mapping-fields.hint-sync-always'),
        CREATE_ONLY: this.$t('page.admin-sync-profiles.mapping-fields.hint-sync-create-only'),
        UPDATE_ONLY: this.$t('page.admin-sync-profiles.mapping-fields.hint-sync-update-only'),
      };
      return map[this.form.syncMode] || null;
    },
    fieldConfigHint() {
      if (this.form.fieldType === 'lookup') {
        const parsed = this.parsedFieldConfig;
        if (parsed && parsed.lookupType === 'contact') {
          return this.$t('page.admin-sync-profiles.mapping-fields.hint-options-lookup-contact');
        }
        if (parsed && parsed.lookupType === 'user') {
          return this.$t('page.admin-sync-profiles.mapping-fields.hint-options-lookup-user');
        }
        return this.$t('page.admin-sync-profiles.mapping-fields.hint-options-lookup-account');
      }
      if (this.form.fieldType === 'choice') {
        return this.$t('page.admin-sync-profiles.mapping-fields.hint-options-choice');
      }
      return null;
    },
    fieldConfigPlaceholder() {
      if (this.form.fieldType === 'lookup') {
        return JSON.stringify(LOOKUP_TEMPLATE);
      }
      if (this.form.fieldType === 'choice') {
        return JSON.stringify(CHOICE_TEMPLATE);
      }
      return '';
    },
    parsedFieldConfig() {
      try {
        return this.form.fieldConfig ? JSON.parse(this.form.fieldConfig) : null;
      } catch {
        return null;
      }
    },

    // ── Validation (always-on — used by isFormInvalid to gate the Save button) ──
    displayOrderHasError() {
      return (
        this.isLookupOrChoice &&
        (this.form.displayOrder === null || this.form.displayOrder === '' || isNaN(this.form.displayOrder))
      );
    },
    labelHasError() {
      return this.isLookupOrChoice && !this.form.label;
    },
    fieldConfigHasError() {
      if (!this.isLookupOrChoice) return false;
      if (!this.form.fieldConfig) return true;
      try {
        JSON.parse(this.form.fieldConfig);
        return false;
      } catch {
        return true;
      }
    },
    valueExpressionHasError() {
      if (!this.form.valueExpression) return true; // always required
      if (this.isLookupOrChoice) {
        // Must be a single wrapped identifier, e.g. {{account}}. Matches back-end validateValueExpression.
        return !/^\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}$/.test(this.form.valueExpression);
      }
      if (this.form.fieldType === 'number') {
        const v = this.form.valueExpression.trim();
        const isTemplate = /\{\{[\s\S]*\}\}/.test(v);
        const isBareNumber = v !== '' && !isNaN(Number(v));
        return !(isTemplate || isBareNumber);
      }
      if (this.isBoolean) {
        // Picker only emits 'true' / 'false'. Mirror the BE's strict parse so a stale
        // mapping that somehow carries a different literal surfaces an error on edit.
        return this.form.valueExpression !== 'true' && this.form.valueExpression !== 'false';
      }
      return false;
    },
    valueExpressionError() {
      if (!this.valueExpressionHasError) return null;
      if (!this.form.valueExpression) {
        return this.submitted || this.touched.valueExpression
          ? this.$t('page.admin-sync-profiles.mapping-fields.value-expression-required')
          : null;
      }
      if (this.isLookupOrChoice) {
        return this.$t('page.admin-sync-profiles.mapping-fields.value-expression-invalid');
      }
      if (this.isBoolean) {
        return this.$t('page.admin-sync-profiles.mapping-fields.value-expression-boolean-error');
      }
      return this.$t('page.admin-sync-profiles.mapping-fields.value-expression-number-error');
    },
    isFormInvalid() {
      return (
        !this.form.crmFieldName ||
        this.valueExpressionHasError ||
        this.form.entityScope.length === 0 ||
        this.displayOrderHasError ||
        this.labelHasError ||
        this.fieldConfigHasError
      );
    },

    // ── Display errors (shown after first save attempt OR once user has touched the field) ──
    crmFieldNameError() {
      if (this.form.crmFieldName) return null;
      return this.submitted || this.touched.crmFieldName
        ? this.$t('page.admin-sync-profiles.mapping-fields.crm-field-name-required')
        : null;
    },
    displayOrderError() {
      if (!this.displayOrderHasError) return null;
      return this.submitted || this.touched.displayOrder
        ? this.$t('page.admin-sync-profiles.mapping-fields.display-order-required')
        : null;
    },
    labelError() {
      if (!this.labelHasError) return null;
      return this.submitted || this.touched.label
        ? this.$t('page.admin-sync-profiles.mapping-fields.label-required')
        : null;
    },
    fieldConfigError() {
      if (!this.isLookupOrChoice) return null;
      // JSON syntax errors are always shown (live feedback while typing)
      if (this.form.fieldConfig) {
        try {
          JSON.parse(this.form.fieldConfig);
          return null;
        } catch {
          return this.$t('page.admin-sync-profiles.mapping-fields.field-config-invalid-json');
        }
      }
      return this.submitted || this.touched.fieldConfig
        ? this.$t('page.admin-sync-profiles.mapping-fields.field-config-required')
        : null;
    },

    // ── Select options ─────────────────────────────────────────────────────
    fieldTypeOptions() {
      return [
        { label: this.$t('page.admin-sync-profiles.field-types.string'), value: 'string' },
        { label: this.$t('page.admin-sync-profiles.field-types.number'), value: 'number' },
        { label: this.$t('page.admin-sync-profiles.field-types.boolean'), value: 'boolean' },
        { label: this.$t('page.admin-sync-profiles.field-types.choice'), value: 'choice' },
        { label: this.$t('page.admin-sync-profiles.field-types.lookup'), value: 'lookup' },
      ];
    },
    syncModeOptions() {
      return [
        { label: this.$t('page.admin-sync-profiles.sync-modes.always'), value: 'ALWAYS' },
        { label: this.$t('page.admin-sync-profiles.sync-modes.create-only'), value: 'CREATE_ONLY' },
        { label: this.$t('page.admin-sync-profiles.sync-modes.update-only'), value: 'UPDATE_ONLY' },
      ];
    },
  },
  watch: {
    isLookupOrChoice(val) {
      if (this.formLoading) return;
      if (val) {
        this.form.displayOrder = this.nextDisplayOrder;
        if (!this.isEdit) {
          this.$delete(this.touched, 'valueExpression');
          this.form.valueExpression = this.wrapAsIdentifier(this.form.crmFieldName);
        }
      } else {
        this.form.displayOrder = 999;
        this.form.label = '';
        this.form.fieldConfig = '';
        this.form.valueExpression = '';
        this.$delete(this.touched, 'valueExpression');
      }
    },
    isBoolean(val) {
      if (this.formLoading) return;
      if (val) {
        // Entering boolean from another type — default to 'true' unless the existing
        // value already happens to be 'true' / 'false' (round-trip preservation on edit).
        if (this.form.valueExpression !== 'true' && this.form.valueExpression !== 'false') {
          this.form.valueExpression = 'true';
          this.$delete(this.touched, 'valueExpression');
        }
      } else {
        // Leaving boolean — clear the leftover literal so the user starts fresh in the
        // new type's input. 'true' / 'false' is meaningless for number, and as a string
        // value it's almost never what the user actually intended.
        if (this.form.valueExpression === 'true' || this.form.valueExpression === 'false') {
          this.form.valueExpression = '';
          this.$delete(this.touched, 'valueExpression');
        }
      }
    },
    'form.crmFieldName'(val) {
      if (this.isLookupOrChoice && !this.isEdit && !this.touched.valueExpression) {
        this.form.valueExpression = this.wrapAsIdentifier(val);
      }
    },
  },
  methods: {
    ...mapActions('adminSyncProfileFieldMappings', ['createFieldMapping', 'updateFieldMapping', 'deleteFieldMapping']),
    sanitizeCrmFieldName(name) {
      return (name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // strip accents (é→e, à→a, ç→c…)
        .toLowerCase()
        .replace(/\s+/g, '_') // spaces → underscore
        .replace(/[^a-z0-9_]/g, '') // remove remaining special chars (incl. . and ?)
        .replace(/_{2,}/g, '_') // collapse consecutive underscores
        .replace(/^(\d)/, '_$1'); // can't start with a digit
    },
    wrapAsIdentifier(name) {
      const sanitized = this.sanitizeCrmFieldName(name);
      return sanitized ? `{{${sanitized}}}` : '';
    },
    onFieldTypeChange(v) {
      this.form.fieldType = v;
    },
    setBooleanValue(v) {
      this.form.valueExpression = v;
      this.touch('valueExpression');
    },
    onValueExpressionInput(v) {
      this.form.valueExpression = v;
      this.touch('valueExpression');
    },
    onInsertVariable() {
      const ref = this.$refs.valueExpressionInput;
      if (ref && typeof ref.insertVariable === 'function') ref.insertVariable();
    },
    fillOptionsTemplate() {
      let template;
      if (this.form.fieldType === 'lookup') {
        const parsed = this.parsedFieldConfig;
        template = parsed && parsed.lookupType === 'contact' ? LOOKUP_TEMPLATE_CONTACT : LOOKUP_TEMPLATE;
      } else {
        template = CHOICE_TEMPLATE;
      }
      this.form.fieldConfig = JSON.stringify(template, null, 2);
    },
    emptyForm() {
      return {
        crmFieldName: '',
        valueExpression: '',
        fieldType: 'string',
        syncMode: 'ALWAYS',
        entityScope: ['opportunity'],
        displayOrder: 999,
        label: '',
        fieldConfig: '',
      };
    },
    touch(field) {
      this.$set(this.touched, field, true);
    },
    openDialog(mapping = null) {
      this.submitted = false;
      this.touched = {};
      this.formLoading = true;
      this.helpOpen = false;
      this.mapping = mapping;
      if (mapping) {
        this.form = {
          crmFieldName: mapping.crmFieldName,
          valueExpression: mapping.valueExpression,
          fieldType: mapping.fieldType,
          syncMode: mapping.syncMode,
          entityScope: mapping.entityScope ? [...mapping.entityScope] : [],
          displayOrder: mapping.displayOrder,
          label: mapping.label || '',
          fieldConfig: mapping.fieldConfig || '',
        };
      } else {
        this.form = this.emptyForm();
      }
      this.$nextTick(() => {
        this.formLoading = false;
      });
      this.$refs.dialog.openModal();
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    onSubmit() {
      this.submitted = true;
      if (this.isFormInvalid) return;
      const payload = {
        ...this.form,
        label: this.isLookupOrChoice ? this.form.label || null : null,
        fieldConfig: this.isLookupOrChoice ? this.form.fieldConfig || null : null,
        displayOrder: this.isLookupOrChoice ? this.form.displayOrder : 999,
      };

      const action = this.isEdit
        ? this.updateFieldMapping({ profileId: this.profileId, mappingId: this.mapping.id, mapping: payload })
        : this.createFieldMapping({ profileId: this.profileId, mapping: payload });

      action
        .then(() => {
          this.$toast.success(this.$t('page.admin-sync-profiles.mapping-dialog.success'));
          this.$emit('action-succeeded');
          this.closeDialog();
        })
        .catch((err) => {
          // Surface back-end error message (e.g. valueExpression validation 400 from
          // OpportunitySyncProfileService.validateValueExpression) verbatim — it includes
          // the offending value which is useful to the user. Fall back to the generic
          // localized message if no message is present.
          const backendMessage =
            (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
            null;
          this.$toast.error(backendMessage || this.$t('page.admin-sync-profiles.mapping-dialog.error'));
        });
    },
  },
};
</script>

<style lang="scss">
.base-dialog__field-mapping-dialog {
  height: auto;
  width: 700px;

  .base-dialog__content {
    padding: 32px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }

  // Consistent field spacing — same approach as SyncProfileDialog.
  .form-input {
    padding-top: 20px;
    padding-bottom: 0;
    margin-bottom: 14px;
  }

  .base-select {
    margin-bottom: 14px;
  }

  // Flow hints and errors in the document (not absolutely positioned) so long text
  // pushes content down instead of overflowing into the field above.
  .form-input__hint,
  .form-input__error-msg {
    position: static;
    display: block;
    margin-top: 4px;
  }
}

.field-mapping-dialog {
  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 0;

    // BaseSelect is hardcoded to height: 90px; allow it to grow when hint wraps
    .base-select {
      height: auto;
      min-height: 90px;
    }
  }

  &__field {
    margin-bottom: 10px;
  }

  &__label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__template-btn {
    font-size: 12px;
    color: $color-accent;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }

  // The value-expression FormInput's top-right slot has two adjacent buttons —
  // "Insert {{ }}" and "Syntax help". Render insert label in monospace so it reads as
  // code, and use a direct margin + pseudo-element divider on the insert button to
  // separate them. Doing it on the button itself sidesteps any specificity / load-order
  // issues with FormInput's own `.form-input__top-right` styles.
  &__insert-var-btn {
    // Code-chip styling — overrides the underlined-link defaults inherited from
    // `__template-btn`. Tight padding/line-height keep the chip from extending past
    // the label-row height into the input area below.
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    background: $color-neutral-pale;
    border: 1px solid $color-border;
    border-radius: 3px;
    padding: 0 5px;
    line-height: 14px;
    text-decoration: none;
    position: relative;
    margin-right: 18px;

    &:hover {
      opacity: 1;
      background: $color-accent-100;
      border-color: $color-accent-300;
      text-decoration: none;
    }

    // Thin vertical divider between this button and the next one.
    &::after {
      content: '';
      position: absolute;
      right: -10px;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 12px;
      background: $color-border;
    }
  }

  &__value-group {
    border: 1px solid $color-border;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .form-input {
      margin-bottom: 0;
      // Bump padding-top above the dialog's default 20px so the chip-style insert
      // button has clearance from the input that starts below it.
      padding-top: 26px;
    }

    // FormInput pins its top-right slot to `right: 0`; the buttons hug the inner edge of
    // the value-group panel. Nudge them in a touch so the chip and link breathe.
    .form-input__top-right {
      right: 4px;
    }
  }

  &__form-section {
    border: 1px solid $color-border;
    border-radius: 6px;
    padding: 10px 14px 0;
    margin-bottom: 10px;

    .field-mapping-dialog__row {
      grid-template-columns: 1fr 2fr;
    }
  }

  &__generated-expression {
    padding-bottom: 4px;
  }

  &__code-input .form-input__input {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    // !important to override FormInput's default `font-size: 16px !important`. 13px
    // balances readability against horizontal room (~25% more chars per line than 16px)
    // and matches the inline <code> chips used in the help drawer.
    font-size: 13px !important;
  }

  &__form-section-title {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $color-normal-pale;
    margin-bottom: 4px;
  }

  // Pill look/behavior comes from BasePillGroup; this class only positions it in the form.
  &__scope-pills {
    margin-top: 8px;
  }

  &__textarea {
    width: 100%;
    border: 1px solid $color-border;
    border-radius: 4px;
    padding: 8px;
    font-size: 13px;
    font-family: monospace;
    resize: vertical;
    background: $color-neutral-pale;
    color: $color-normal;

    &:focus {
      outline: none;
      border-color: $color-accent;
    }

    &--error {
      border-color: $color-error;
    }
  }

  &__hint {
    font-size: 12px;
    color: $color-normal-pale;
    margin-top: 4px;
    line-height: 1.4;
  }

  &__error {
    font-size: 12px;
    color: $color-error;
    margin-top: 3px;
    line-height: 1.4;
  }

  &__warning {
    font-size: 12px;
    color: $color-yellow;
    margin-top: 4px;
    line-height: 1.4;
  }

  &__actions {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__actions-right {
    display: flex;
    gap: 12px;
  }
}
</style>
